import { Tool } from "./AssistantDetailsContent";
import { useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { PeriodicTasksManager } from "./PeriodicTasksManager";
import { useSpotUser } from "@/utils/spotUser";
import {
  AttachFileSettings,
  HumanInTheLoopSettingsInput,
  KnowledgeBaseSelectorSettingsInput,
  NoxusQaSettingsInput,
  WebResearchSettingsInput,
  MemorySettingsInput,
  SafetyService,
} from "@/generated";
import { useQuery } from "@tanstack/react-query";
import { getIcon } from "@/utils/assistants";
import { FFService } from "@/services/FeatureFlags/FeatureFlags";
import { Switch } from "../shadcn/switch";
import { Button } from "../shadcn/button";
import {
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  TerminalSquare,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ExpandableTextarea } from "./ExpandableTextarea";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { SandboxExplorer } from "./SandboxExplorer";
import { RichTextValue } from "@/components/Form/RichTextField";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { ConfigFormRenderer } from "@/components/Form/ConfigFormRenderer";
import { useFormContext } from "react-hook-form";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import { ReleaseStatusBadge } from "@/components/UI/ReleaseStatusBadge";
import { ToolSettingsDialog } from "./ToolSettingsDialog";

// Tools that should NOT show approval controls
const NO_APPROVAL_TOOLS = new Set([
  "attach_file",
  "memory",
  "human_in_the_loop",
]);

// Fields rendered separately (not via dynamic config renderer)
const SKIP_FIELDS = new Set([
  "type",
  "title",
  "description",
  "category",
  "enabled",
  "visible",
  "icon",
  "bg_color",
  "icon_color",
  "tool_display_icon",
  "tool_display_message_start",
  "tool_display_message_end",
  "extra_instructions",
  "requires_approval",
  "settings_display",
]);

export const QuickActionField = ({
  tools,
  onToolsChange,
  isNodeConfig,
  isDisabled,
  itemConfig,
  itemConfigByType,
}: {
  tools: Tool[] | undefined;
  onToolsChange: (tools: Tool[]) => void;
  isNodeConfig?: boolean;
  isDisabled?: boolean;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
  itemConfigByType?: Record<string, Record<string, NoxusFormEntryConfig>>;
}) => {
  const IS_TOOL_APPROVALS_ENABLED = FFService.getVar(
    "AGENT_TOOL_APPROVALS_ENABLED"
  );

  // Tool types hidden by the platform kill-switch or by a tenant flag being
  // off; hidden here so authors don't configure a tool that will no-op.
  const { data: disabledToolsData } = useQuery({
    queryKey: ["disabled-agent-tools"],
    queryFn: () => SafetyService.getDisabledAgentTools(),
    staleTime: 60_000,
  });
  const hiddenToolTypes = useMemo(
    () => new Set(disabledToolsData?.data?.disabled ?? []),
    [disabledToolsData]
  );
  const betaToolTypes = useMemo(
    () => new Set(disabledToolsData?.data?.beta ?? []),
    [disabledToolsData]
  );

  const quick_action_tools = useMemo(() => {
    return tools?.filter(
      (tool) =>
        tool.category === "QuickAction" &&
        !hiddenToolTypes.has(tool.type as string) &&
        tool.visible !== false
    ) as (
      | AttachFileSettings
      | KnowledgeBaseSelectorSettingsInput
      | NoxusQaSettingsInput
      | WebResearchSettingsInput
      | HumanInTheLoopSettingsInput
      | MemorySettingsInput
    )[];
  }, [tools, hiddenToolTypes]);

  const updateTool = (toolType: string, patch: Record<string, unknown>) => {
    if (isDisabled) return;
    const newTools = tools?.map((t) =>
      t.type === toolType ? { ...t, ...patch } : t
    );
    if (newTools) onToolsChange(newTools);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-foreground leading-none">
            {isNodeConfig ? "Base tools" : "Tools"}
          </p>
          <p className="text-xs font-regular text-muted-foreground mt-1.5">
            Select the tools you want to enable on your agent.
          </p>
        </div>

        {quick_action_tools
          ?.filter(
            (t) =>
              t.type !== "human_in_the_loop" &&
              (t.type as string) !== "agent_memory"
          )
          .map((tool) => (
            <QuickActionEntry
              key={tool.type}
              tool={tool}
              toolIndex={tools?.findIndex((t) => t.type === tool.type) ?? 0}
              showApproval={
                !!IS_TOOL_APPROVALS_ENABLED &&
                !NO_APPROVAL_TOOLS.has(tool.type as string)
              }
              isBeta={betaToolTypes.has(tool.type as string)}
              onUpdate={(patch) => updateTool(tool.type as string, patch)}
              isDisabled={isDisabled}
              fieldConfig={itemConfig?.["extra_instructions"]}
              toolConfig={itemConfigByType?.[tool.type as string]}
              formFieldPrefix="definition.base_tools"
            />
          ))}
      </div>
    </div>
  );
};

const QuickActionEntry = ({
  tool,
  toolIndex,
  showApproval,
  isBeta,
  onUpdate,
  isDisabled,
  fieldConfig,
  toolConfig,
  formFieldPrefix,
}: {
  tool: Tool;
  toolIndex: number;
  showApproval: boolean;
  isBeta: boolean;
  onUpdate: (patch: Record<string, unknown>) => void;
  isDisabled?: boolean;
  fieldConfig?: NoxusFormEntryConfig;
  toolConfig?: Record<string, NoxusFormEntryConfig>;
  formFieldPrefix?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [shellDialogExpanded, setShellDialogExpanded] = useState(false);
  const form = useFormContext();
  const { spotGroup } = useSpotUser();
  const enabled = tool.enabled;
  const toolType = tool.type as string;
  // A tool with many options declares that they open in a dialog; it opens
  // whether or not the tool is on, so it can be set up before switching on.
  const opensDialog =
    (tool as Record<string, unknown>).settings_display === "dialog";
  const hasSettings = enabled && toolType !== "attach_file" && !opensDialog;

  // Build a minimal Assistant from the surrounding form context so the
  // SandboxExplorer can resolve the right backend websocket. The form
  // is the canonical TypedAssistant when QuickActionField is rendered
  // inside AssistantDetailsContent; in the workflow node config drawer
  // it isn't, in which case `id` is undefined and we hide the button.
  const assistantId = form?.getValues?.("id") as string | undefined;
  const assistantName = form?.getValues?.("name") as string | undefined;
  const canOpenShell =
    toolType === "sandbox" && enabled && !!assistantId && !!assistantName;

  // The open shell lives in the URL, as the open section does, so a link (or
  // Genie) can open it.
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const shellDialogOpen =
    canOpenShell && searchParams?.get("shell") === "sandbox";
  const setShellDialogOpen = (open: boolean) => {
    const params = new URLSearchParams(searchParams?.toString());
    if (open) params.set("shell", "sandbox");
    else params.delete("shell");
    router.replace(`${pathname}?${params.toString()}`);
  };

  // Build the field key prefix for this specific tool in the form
  // e.g. "definition.base_tools.2.timeout"
  const toolFieldPrefix = formFieldPrefix
    ? `${formFieldPrefix}.${toolIndex}`
    : undefined;

  const toolFieldConfig = useMemo(() => {
    if (!toolConfig) return undefined;
    const entries = Object.entries(toolConfig)
      .filter(([key, cfg]) => !SKIP_FIELDS.has(key) && cfg.display)
      .map(
        ([key, cfg]) => [key, { ...cfg, visible: cfg.visible ?? true }] as const
      );
    return entries.length ? Object.fromEntries(entries) : undefined;
  }, [toolConfig]);

  return (
    <Collapsible open={isOpen && hasSettings} onOpenChange={setIsOpen}>
      <div className="border border-border rounded-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <CollapsibleTrigger asChild disabled={!hasSettings && !opensDialog}>
            <button
              type="button"
              className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
              onClick={
                opensDialog ? () => setSettingsDialogOpen(true) : undefined
              }
            >
              <div
                className="flex p-1.5 rounded-md justify-center items-center shrink-0"
                style={{
                  backgroundColor: (tool as any).bg_color || "#eff6ff",
                  color: (tool as any).icon_color || "#3b82f6",
                }}
              >
                {getIcon((tool as any).icon)}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">
                    {tool.title || ""}
                  </p>
                  {isBeta && (
                    <ReleaseStatusBadge status="beta" className="shrink-0" />
                  )}
                </div>
                {tool.description && (
                  <p className="text-xs text-muted-foreground truncate">
                    {tool.description}
                  </p>
                )}
              </div>
              {hasSettings && (
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-muted-foreground shrink-0 transition-transform",
                    isOpen && "rotate-180"
                  )}
                />
              )}
              {opensDialog && toolFieldConfig && (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </button>
          </CollapsibleTrigger>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => onUpdate({ enabled: checked })}
            disabled={isDisabled}
          />
        </div>

        {/* Expanded settings */}
        <CollapsibleContent>
          {/* Tool usage conditions — AI TipTap editor */}
          <ExpandableTextarea
            value={tool.extra_instructions || ""}
            onChange={(value) => onUpdate({ extra_instructions: value })}
            isDisabled={isDisabled}
            fieldConfig={fieldConfig}
            defaultExpanded
          />

          {toolFieldConfig && (
            <div className="border-t border-border/50 px-3 py-4">
              <ConfigFormRenderer
                config={toolFieldConfig}
                form={form}
                fieldPrefix={toolFieldPrefix}
                isReadOnly={isDisabled}
              />
            </div>
          )}

          {/* Periodic tasks manager — only for the schedule_tool */}
          {toolType === "schedule_tool" && assistantId && spotGroup?.id && (
            <PeriodicTasksManager
              groupId={spotGroup.id}
              assistantId={assistantId}
            />
          )}

          {/* Open interactive shell — only for the sandbox tool, only
              when we know the assistant id from the surrounding form.
              Matches the typography of the surrounding rows
              (text-xs font-medium text-muted-foreground for labels). */}
          {canOpenShell && (
            <div className="border-t border-border/50 px-3 py-2 flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Interactive shell
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShellDialogOpen(true)}
              >
                <TerminalSquare className="h-3 w-3" />
                Open shell
              </Button>
            </div>
          )}

          {/* Approval toggle */}
          {showApproval && (
            <div className="border-t border-border/50 px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Require approval
              </span>
              <Switch
                checked={(tool as any).requires_approval ?? false}
                onCheckedChange={(v) => onUpdate({ requires_approval: v })}
                disabled={isDisabled}
                className="scale-75"
              />
            </div>
          )}
        </CollapsibleContent>
      </div>
      {opensDialog && toolFieldConfig && (
        <ToolSettingsDialog
          isOpen={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
          title={tool.title || ""}
          description={tool.description || undefined}
          config={toolFieldConfig}
          form={form}
          fieldPrefix={toolFieldPrefix}
          isReadOnly={isDisabled}
        />
      )}
      {/* Single Dialog that toggles between compact and fullscreen by
          swapping className on DialogContent. SandboxExplorer is
          rendered in one mount point so the fullscreen toggle does
          NOT remount it — otherwise the active WebSocket would drop
          and the shell would have to reconnect every time the user
          expands/collapses. */}
      {canOpenShell && (
        <Dialog
          open={shellDialogOpen}
          onOpenChange={(open) => {
            setShellDialogOpen(open);
            if (!open) setShellDialogExpanded(false);
          }}
        >
          <DialogContent
            showCloseButton={false}
            size={shellDialogExpanded ? "full" : "4xl"}
            className={cn(
              "flex flex-col gap-0 overflow-hidden p-0",
              shellDialogExpanded
                ? // True fullscreen: override every size/position
                  // constraint DialogContent normally applies. Deliberately
                  // covers the Genie panel too, hence the viewport units.
                  "!fixed !inset-0 !top-0 !left-0 !translate-x-0 !translate-y-0 " +
                    // eslint-disable-next-line no-restricted-syntax
                    "!max-w-full !max-h-full !w-screen !h-screen " +
                    "!rounded-none !border-0 !grid-cols-1"
                : "h-[min(90%,640px)]"
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b bg-card px-4 py-2">
              <DialogTitle className="text-sm font-medium text-foreground">
                Sandbox shell:{" "}
                <span className="text-muted-foreground">{assistantName}</span>
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="iconsm"
                  variant="ghost"
                  onClick={() => setShellDialogExpanded((v) => !v)}
                  title={shellDialogExpanded ? "Exit fullscreen" : "Fullscreen"}
                >
                  {shellDialogExpanded ? (
                    <Minimize2 className="h-3.5 w-3.5" />
                  ) : (
                    <Maximize2 className="h-3.5 w-3.5" />
                  )}
                </Button>
                <DialogClose asChild>
                  <Button
                    type="button"
                    size="iconsm"
                    variant="ghost"
                    title="Close"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </DialogClose>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <SandboxExplorer
                shellPath={`/conversation/assistants/${assistantId}/shell`}
                resetPath={`/groups/${spotGroup.id}/assistants/${assistantId}/sandbox/reset`}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Collapsible>
  );
};
